import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { useLoyaltyServiceRewardQuery } from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useRefetchOnLoyaltyUserChange } from 'state/loyalty/hooks/use-refetch-on-loyalty-user-change';
import { IEngineRewardsMap, LoyaltyReward } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';

import { mapServiceEngineReward, mapServiceReward } from '../utils';
import { getLoyaltyServiceRewardServiceMode } from '../utils/service-mode';

interface IUseLoyaltyServiceRewardOptions {
  loyaltyEngineId?: string;
  skip?: boolean;
}

export function useLoyaltyServiceReward({
  loyaltyEngineId,
  skip = false,
}: IUseLoyaltyServiceRewardOptions) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const variables = {
    id: loyaltyEngineId ?? '',
    omitInvalids: false,
    serviceMode: getLoyaltyServiceRewardServiceMode(serviceMode),
    storeId: store?.number,
  };

  const querySkip = skip || loyaltyUserIdLoading || !loyaltyEngineId;

  const { data, error, loading: queryLoading, refetch } = useLoyaltyServiceRewardQuery({
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: querySkip,
  });

  const {
    dataForCurrentIdentity,
    identityTransitionLoading,
    refetchForCurrentIdentity,
  } = useRefetchOnLoyaltyUserChange({
    loyaltyUserId,
    queryData: data,
    queryError: error,
    queryKey: JSON.stringify(variables),
    queryLoading,
    refetch,
    skip: querySkip,
  });

  const serviceReward = dataForCurrentIdentity?.loyaltyServiceReward;

  const { selectedReward, engineRewardsMap } = useMemo<{
    selectedReward?: LoyaltyReward;
    engineRewardsMap: IEngineRewardsMap;
  }>(() => {
    if (!serviceReward) {
      return { selectedReward: undefined, engineRewardsMap: {} };
    }

    return {
      selectedReward: mapServiceReward(serviceReward),
      engineRewardsMap: {
        [serviceReward.id]: mapServiceEngineReward(serviceReward),
      },
    };
  }, [serviceReward]);

  const refreshReward = useCallback(async () => {
    if (skip || loyaltyUserIdLoading || !loyaltyEngineId) {
      return;
    }

    return refetchForCurrentIdentity();
  }, [loyaltyEngineId, loyaltyUserIdLoading, refetchForCurrentIdentity, skip]);

  const loading =
    !skip &&
    (loyaltyUserIdLoading ||
      Boolean(loyaltyEngineId && (queryLoading || identityTransitionLoading)));

  return {
    selectedReward,
    engineRewardsMap,
    error: identityTransitionLoading ? undefined : error,
    loading,
    refreshReward,
  };
}
